import React, { useEffect, useRef, useState } from 'react'
import { dropDownSelectionStyles, getValueFromResource } from '../../../common/timeLiveFunction'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import clsx from 'clsx';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import infoSVG from '../../../assets/image/info-SVG.svg';
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShowToast } from '../../../common/Toastr';
import { TOOLTIP_STYLE } from '../../../common/TooltipStyle';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BiometricDeviceConnectivity from './BiometricDeviceConnectivity';

interface OptionType {
    value: string;
    label: string;
}

type ConfigureStatus = 'idle' | 'loading' | 'success' | 'failed';

interface TestStepResult {
    key: string;
    label: string;
    description: string;
    status: 'success' | 'failed' | 'pending';
    durationMs: number | null;
}

interface ConnectivityTestResult {
    isConnected: boolean;
    responseTimeMs: number;
    deviceReachable: boolean;
    tcpPortStatus: 'Open' | 'Closed';
    integrationServiceReachable: boolean;
    lastSuccessfulTest: string | null;
    deviceIp: string;
    devicePort: string;
    deviceSerialNumber: string;
    firmwareVersion: string;
    connectionQuality: 'Excellent' | 'Good' | 'Fair' | 'Poor';
    networkLatencyMs: number;
    integrationServiceStatus: 'Reachable' | 'Unreachable';
    integrationServiceMessage: string;
    totalDurationMs: number;
    steps: TestStepResult[];
}

const serverModeOptions: OptionType[] = [
    { value: 'ADMS', label: 'ADMS' },
];

const communicationProtocolOptions: OptionType[] = [
    { value: 'TCP', label: 'TCP' },
    { value: 'UDP', label: 'UDP' },
    { value: 'HTTPS', label: 'HTTPS' },
];

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const emptyStringToUndefined = (value: any, originalValue: any) =>
    String(originalValue)?.trim() === '' ? undefined : value;

interface FormValues {
    deviceName: string;
    deviceIdentifier: string;
    deviceModel: string;
    deviceIpAddress: string;
    devicePort: string;
    communicationPassword: string;
    latitude: string;
    longitude: string;
    timeZone: string | number | null;
    serverMode: OptionType | null;
    serverAddress: string;
    serverPort: string;
    pollingInterval: string;
    communicationProtocol: OptionType | null;
    isActive: boolean;
}

const initialValues: FormValues = {
    deviceName: '',
    deviceIdentifier: '',
    deviceModel: '',
    deviceIpAddress: '',
    devicePort: '',
    communicationPassword: '',
    latitude: '',
    longitude: '',
    timeZone: null,
    serverMode: serverModeOptions[0],
    serverAddress: '',
    serverPort: '',
    pollingInterval: '',
    communicationProtocol: communicationProtocolOptions[0],
    isActive: true,
};

const validationSchema = Yup.object({
    deviceName: Yup.string().trim().required('Device Name is required.'),
    deviceIdentifier: Yup.string().trim().required('Device Identifier is required.'),
    deviceModel: Yup.string().trim().required('Device Model is required.'),
    timeZone: Yup.mixed().required('Time Zone is required.'),

    // deviceIpAddress: Yup.string().trim().required('Device IP Address is required.'),

    // devicePort: Yup.number()
    //     .transform(emptyStringToUndefined)
    //     .typeError('Device Port must be a number.')
    //     .required('Device Port is required.')
    //     .integer('Device Port must be a whole number.')
    //     .min(1, 'Device Port must be between 1 and 65535.')
    //     .max(65535, 'Device Port must be between 1 and 65535.'),

    latitude: Yup.number()
        .transform(emptyStringToUndefined)
        .typeError('Latitude must be a valid number.')
        .min(-90, 'Latitude must be between -90 and 90.')
        .max(90, 'Latitude must be between -90 and 90.')
        .nullable(),

    longitude: Yup.number()
        .transform(emptyStringToUndefined)
        .typeError('Longitude must be a valid number.')
        .min(-180, 'Longitude must be between -180 and 180.')
        .max(180, 'Longitude must be between -180 and 180.')
        .nullable(),
});

const BiometericDeviceConfiguration = () => {
    const dispatch = useAppDispatch();
    const { timeZoneId } = useAppSelector((state: any) => state.globalState);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const id = location.pathname.split("/")?.[2];
    const originalDeviceIdentifier = useRef<string>('');
    const [timezoneOptions, setTimezoneOptions] = useState([]);
    const [deviceStatus, setDeviceStatus] = useState<string>('Offline');
    const [configureStatus, setConfigureStatus] = useState<ConfigureStatus>('idle');
    const [lastStatusCheckedOn, setLastStatusCheckedOn] = useState<string | null>(null);
    const [connectivityResult, setConnectivityResult] = useState<ConnectivityTestResult | null>(null);
    const [initialFormValues, setInitialFormValues] = useState<FormValues>(initialValues);

    const formik = useFormik<FormValues>({
        initialValues,
        validationSchema,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: async (values) => {
            if (id && values.deviceIdentifier?.trim() !== originalDeviceIdentifier.current?.trim()) {
                ShowToast('Error', 'Device Identifier cannot be changed.', "error", "toast-top-right");
                formik.setFieldValue('deviceIdentifier', originalDeviceIdentifier.current);
                return;
            }
            const payload = {
                deviceName: values.deviceName,
                deviceIdentifier: values.deviceIdentifier,
                deviceModel: values.deviceModel,
                deviceIpAddress: values.deviceIpAddress,
                devicePort: Number(values.devicePort),
                communicationPassword: values.communicationPassword,
                latitude: values.latitude?.trim() === '' ? null : Number(values.latitude),
                longitude: values.longitude?.trim() === '' ? null : Number(values.longitude),
                TimeZoneId: values.timeZone,
                serverMode: values.serverMode?.value,
                serverAddress: values.serverAddress,
                serverPort: values.serverPort?.trim() === '' ? null : Number(values.serverPort),
                pollingInterval: values.pollingInterval?.trim() === '' ? null : Number(values.pollingInterval),
                communicationProtocol: values.communicationProtocol?.value,
                deviceStatus,
                isActive: values.isActive,
                LastStatusCheckedOn: lastStatusCheckedOn,
            };

            try {
                let response;
                if (id) {
                    const updatePayload = {
                        BiometricDeviceId: id,
                        DeviceName: values.deviceName,
                        DeviceIdentifier: values.deviceIdentifier,
                        deviceIpAddress: values.deviceIpAddress,
                        devicePort: Number(values.devicePort),
                        isActive: values.isActive
                    };
                    response = await fetchTimeliveAPI(`api/BiometricConfiguration/UpdateAccountBiometricConfiguration`, `PUT`, updatePayload, true);
                } else {
                    response = await fetchTimeliveAPI(`api/BiometricConfiguration/AddAccountBiometricConfiguration`, `POST`, payload, true);
                }
                const json = await response.json();
                if (json?.StatusCode === '201' || json?.StatusCode === '200') {
                    ShowToast('Success', json?.ResponseMessage, "success", "toast-top-right");
                    navigate('/biometricconfigurationlist');
                    handleCancel();
                } else {
                    ShowToast('Error', json?.ResponseMessage, "error", "toast-top-right");
                }
            } catch (error) {
                console.log(error);
            }
        },
    });

    const isConfigureVisible = formik.values.deviceIpAddress?.trim() !== '' && formik.values.devicePort?.trim() !== '';

    const getBiometricDeviceById = async () => {
        try {
            const response = await fetchTimeliveAPI(`api/BiometricConfiguration/GetAccountBiometricDeviceById/${id}`, `Get`, '', true);
            const json = await response.json();
            if (json?.StatusCode === '200') {
                const data = json?.Data;
                originalDeviceIdentifier.current = data?.DeviceIdentifier ?? '';
                const loadedValues: FormValues = {
                    ...formik.values,
                    deviceName: data?.DeviceName ?? '',
                    deviceIdentifier: data?.DeviceIdentifier ?? '',
                    deviceModel: data?.DeviceModel ?? '',
                    deviceIpAddress: data?.DeviceIPAddress ?? '',
                    devicePort: data?.DevicePort != null ? String(data.DevicePort) : '',
                    latitude: data?.Latitude != null ? String(data.Latitude) : '',
                    longitude: data?.Longitude != null ? String(data.Longitude) : '',
                    timeZone: data?.TimeZoneId ?? null,
                    serverAddress: data?.ServerAddress ?? '',
                    serverPort: data?.ServerPort != null ? String(data.ServerPort) : '',
                    pollingInterval: data?.PollingInterval != null ? String(data.PollingInterval) : '',
                    isActive: !!data?.IsActive,
                };

                formik.setValues(loadedValues);
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (id) {
            getBiometricDeviceById();
        }
    }, [])

    const handleIpChange = (value: string) => {
        formik.setFieldValue('deviceIpAddress', value);
        setConfigureStatus('idle');
        setLastStatusCheckedOn(null);
    };

    const handlePortChange = (value: string) => {
        formik.setFieldValue('devicePort', value);
        setConfigureStatus('idle');
        setLastStatusCheckedOn(null);
    };

    const handleConfigureDevice = async () => {
        if (!formik.values.deviceIpAddress?.trim() || !formik.values.devicePort?.trim()) return;

        setConfigureStatus('loading');
        setConnectivityResult(null);

        try {
            const payload = {
                BiometricDeviceId: id ? Number(id) : 0,
                DeviceIPAddress: formik.values.deviceIpAddress,
                DevicePort: Number(formik.values.devicePort)
            };
            const [response] = await Promise.all([
                fetchTimeliveAPI("api/BiometricConfiguration/TestDeviceConnectivity", "POST", payload, true),
                delay(1500)
            ]);

            const json = await response.json();
            const d = json?.Data;

            if (d) {
                const result: ConnectivityTestResult = {
                    isConnected: !!d?.IsConnected,
                    responseTimeMs: d?.ResponseTimeMs ?? 0,
                    deviceReachable: !!d?.DeviceReachable,
                    tcpPortStatus: d?.TcpPortStatus ?? 'Closed',
                    integrationServiceReachable: !!d?.IntegrationServiceReachable,
                    lastSuccessfulTest: d?.LastSuccessfulTest ?? null,
                    deviceIp: d?.DeviceIp ?? formik.values.deviceIpAddress,
                    devicePort: d?.DevicePort ?? formik.values.devicePort,
                    deviceSerialNumber: d?.DeviceSerialNumber ?? '',
                    firmwareVersion: d?.FirmwareVersion ?? '',
                    connectionQuality: d?.ConnectionQuality ?? 'Poor',
                    networkLatencyMs: d?.NetworkLatencyMs ?? 0,
                    integrationServiceStatus: d?.IntegrationServiceStatus ?? 'Unreachable',
                    integrationServiceMessage: d?.IntegrationServiceMessage ?? '',
                    totalDurationMs: d?.TotalDurationMs ?? 0,
                    steps: (d?.Steps ?? []).map((s: any) => ({
                        key: s?.Key,
                        label: s?.Label,
                        description: s?.Description,
                        status: s?.Status,
                        durationMs: s?.DurationMs ?? null,
                    })),
                };

                setConnectivityResult(result);

                if (result.isConnected) {
                    ShowToast('Success', json?.ResponseMessage, "success", "toast-top-right");
                    setConfigureStatus('success');
                    setDeviceStatus('Online');
                    setLastStatusCheckedOn(result.lastSuccessfulTest ?? new Date().toISOString());
                } else {
                    ShowToast('Error', json?.ResponseMessage, "error", "toast-top-right");
                    setConfigureStatus('failed');
                    setDeviceStatus('Offline');
                    setLastStatusCheckedOn(null);
                }
            } else {
                ShowToast('Error', json?.ResponseMessage, "error", "toast-top-right");
                setConfigureStatus('failed');
                setLastStatusCheckedOn(null);
            }
        } catch (error) {
            console.log(error);
            setConfigureStatus('failed');
            setLastStatusCheckedOn(null);
        }
    };

    // const handleConfigureDevice = async () => {
    //     if (!formik.values.deviceIpAddress?.trim() || !formik.values.devicePort?.trim()) return;

    //     setConfigureStatus('loading');

    //     try {
    //         const payload = {
    //             BiometricDeviceId: id,
    //             DeviceIPAddress: formik.values.deviceIpAddress,
    //             DevicePort: formik.values.devicePort
    //         };
    //         const [response] = await Promise.all([
    //             fetchTimeliveAPI("api/BiometricConfiguration/TestDeviceConnectivity", "POST", payload, true),
    //             delay(3000)
    //         ]);

    //         const json = await response.json();

    //         if (json?.Data) {
    //             ShowToast('Success', json?.ResponseMessage, "success", "toast-top-right");
    //             setConfigureStatus('success');
    //             setLastStatusCheckedOn(new Date().toISOString());
    //         } else {
    //             ShowToast('Error', json?.ResponseMessage, "error", "toast-top-right");
    //             setConfigureStatus('failed');
    //             setLastStatusCheckedOn(null);
    //         }
    //     } catch (error) {
    //         console.log(error);
    //         setConfigureStatus('failed');
    //         setLastStatusCheckedOn(null);
    //     }
    // };

    const handleCancel = () => {
        const currentDeviceIdentifier = formik.values.deviceIdentifier;
        formik.resetForm({ values: initialValues });
        formik.setFieldValue('deviceIdentifier', currentDeviceIdentifier);
        if (timeZoneId) {
            formik.setFieldValue('timeZone', timeZoneId);
        }
        setDeviceStatus('Offline');
        setConfigureStatus('idle');
        setLastStatusCheckedOn(null);
    };

    const getTimezone = async () => {
        try {
            const response = await fetchTimeliveAPI('api/Timezone', "GET", '', true);
            const json = await response?.json();
            const options = json?.Data?.map((item: any) => ({
                value: item?.SystemTimeZoneId,
                label: item?.SystemTimeZone
            }))
            setTimezoneOptions(options);
            if (options?.length > 0 && !id) {
                if (timeZoneId) {
                    formik.setFieldValue('timeZone', timeZoneId);
                } else {
                    formik.setFieldValue('timeZone', options[0]?.value);
                }
            }
            // if (options?.length > 0 && !id) {
            //     const matchedOption = options.find((item: any) => item?.value === timeZoneId);
            //     formik.setFieldValue('timeZone', matchedOption?.value ?? options[0]?.value);
            // }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        getTimezone();
    }, [])

    const FieldError = ({ name }: { name: keyof FormValues }) => {
        const error = formik.errors[name];
        const touched = formik.touched[name];
        if (!touched || !error) return null;
        return <div className="text-danger fs-8 mt-1">{error as string}</div>;
    };

    return (
        <div>
            <div className="card card-custom admin-card">
                <div className="card-header card-drop sticky-top bg-white fixed-top">
                    <h3 className="card-title fw-bold headerTitle d-flex align-items-center gap-1">
                        {getValueFromResource('Biometric Device Configuration', dispatch, t)}
                    </h3>
                    <div className="card-toolbar align-items-center gap-2">
                        {/* <button
                            type="button"
                            className={clsx("btn btn-sm btn-success")}
                            disabled={!isConfigureVisible || configureStatus === 'loading'}
                            onClick={handleConfigureDevice}
                        >
                            {configureStatus === 'loading' ? 'Testing...' : 'Test Connectivity'}
                        </button> */}
                        <button
                            type="button"
                            className="btn btn-secondary  btn-bs-btn-font-weight btn-sm"
                            onClick={handleCancel}
                        >
                            Reset
                        </button>

                        <button
                            type="submit"
                            className="btn btn-primary  btn-bs-btn-font-weight btn-sm"
                            onClick={() => formik.handleSubmit()}
                        >
                            {id ? "Update Device" : "Save Device"}
                        </button>
                    </div>
                </div>
                <div className="card-body my-4">
                    <form onSubmit={formik.handleSubmit} noValidate className='border rounded-3 p-4' style={{boxShadow: '0px 0px 0px #e7e4e4'}}>
                        <label className="fs-6 mb-1 fw">Device Information</label>
                        <div className="row">

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Device Name <span className="text-danger">*</span>
                                    <div
                                        style={{ marginBottom: "2px" }}
                                        data-tooltip-id="tooltip-description"
                                        data-tooltip-content={getValueFromResource('Friendly name to identify the biometric device (e.g., Head Office Reception)', dispatch, t)}
                                    >
                                        <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                    </div>
                                </label>
                                <input
                                    name="deviceName"
                                    value={formik.values.deviceName}
                                    type="text"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.deviceName && formik.errors.deviceName && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <FieldError name="deviceName" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Device Identifier <span className="text-danger">*</span>
                                    <div
                                        style={{ marginBottom: "2px" }}
                                        data-tooltip-id="tooltip-description"
                                        data-tooltip-content={getValueFromResource('Unique device serial number used to identify the device', dispatch, t)}
                                    >
                                        <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                    </div>
                                </label>
                                <input
                                    name="deviceIdentifier"
                                    value={formik.values.deviceIdentifier}
                                    type="text"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.deviceIdentifier && formik.errors.deviceIdentifier && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={id ? true : false}
                                />
                                <FieldError name="deviceIdentifier" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Device Model  <span className="text-danger">*</span>
                                    <div
                                        style={{ marginBottom: "2px" }}
                                        data-tooltip-id="tooltip-description"
                                        data-tooltip-content={getValueFromResource('Biometric device model (e.g., ZKTeco SenseFace M2F-LR)', dispatch, t)}
                                    >
                                        <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                    </div>
                                </label>
                                <input
                                    name="deviceModel"
                                    value={formik.values.deviceModel}
                                    type="text"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.deviceModel && formik.errors.deviceModel && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <FieldError name="deviceModel" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Time Zone <span className="text-danger">*</span>
                                    <div
                                        style={{ marginBottom: "2px" }}
                                        data-tooltip-id="tooltip-description"
                                        data-tooltip-content={getValueFromResource('Time zone used to normalize biometric device timestamps.', dispatch, t)}
                                    >
                                        <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                    </div>
                                </label>
                                <Select
                                    className="basic-single fs-7 fw-normal"
                                    classNamePrefix="select"
                                    name="timeZone"
                                    value={timezoneOptions?.find((item: any) => item?.value === formik.values.timeZone) ?? null}
                                    options={timezoneOptions}
                                    styles={dropDownSelectionStyles}
                                    onChange={(option: any) => formik.setFieldValue('timeZone', option?.value ?? null)}
                                    onBlur={() => formik.setFieldTouched('timeZone', true)}
                                />
                                <FieldError name="timeZone" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Device IP Address
                                </label>
                                <input
                                    name="deviceIpAddress"
                                    value={formik.values.deviceIpAddress}
                                    type="text"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.deviceIpAddress && formik.errors.deviceIpAddress && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={(e) => handleIpChange(e.target.value)}
                                    onBlur={formik.handleBlur}
                                />
                                <FieldError name="deviceIpAddress" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Device Port
                                </label>
                                <input
                                    name="devicePort"
                                    value={formik.values.devicePort}
                                    type="text"
                                    inputMode="numeric"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.devicePort && formik.errors.devicePort && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={(e) => handlePortChange(e.target.value)}
                                    onBlur={formik.handleBlur}
                                />
                                <FieldError name="devicePort" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Latitude
                                    <div
                                        style={{ marginBottom: "2px" }}
                                        data-tooltip-id="tooltip-description"
                                        data-tooltip-content={getValueFromResource('Latitude coordinates of the device location.', dispatch, t)}
                                    >
                                        <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                    </div>
                                </label>
                                <input
                                    name="latitude"
                                    value={formik.values.latitude}
                                    type="text"
                                    inputMode="decimal"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.latitude && formik.errors.latitude && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <FieldError name="latitude" />
                            </div>

                            <div className="col-md-6 mb-2">
                                <label className="form-label d-flex align-items-center gap-1">
                                    Longitude
                                    <div
                                        style={{ marginBottom: "2px" }}
                                        data-tooltip-id="tooltip-description"
                                        data-tooltip-content={getValueFromResource('Longitude coordinates of the device location.', dispatch, t)}
                                    >
                                        <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                    </div>
                                </label>
                                <input
                                    name="longitude"
                                    value={formik.values.longitude}
                                    type="text"
                                    inputMode="decimal"
                                    className={clsx("form-control fs-7 fw-normal", formik.touched.longitude && formik.errors.longitude && "is-invalid")}
                                    autoCapitalize="none"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                <FieldError name="longitude" />
                            </div>

                            <div className="col-md-6 mb-3 d-flex align-items-center">
                                <div className="form-check form-switch mt-2">
                                    <input
                                        className="form-check-input custom-padding-right mb-2 h-15px w-30px"
                                        type="checkbox"
                                        checked={formik.values.isActive}
                                        id="LockApprovedRecords"
                                        onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                                    />

                                    <label className="form-check-label d-flex align-items-center gap-1">
                                        Is Active
                                        <div
                                            style={{ marginBottom: "2px" }}
                                            data-tooltip-id="tooltip-description"
                                            data-tooltip-content={getValueFromResource('Enables or disables attendance synchronization from this device. Disabled devices will not pull or process attendance data.', dispatch, t)}
                                        >
                                            <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </form>
                    {/* {isConfigureVisible && connectivityResult && (
                        <BiometricDeviceConnectivity connectivityResult={connectivityResult} />
                    )} */}
                </div>
            </div>
            <ReactTooltip
                id="tooltip-description"
                style={TOOLTIP_STYLE}
                place="top"
                delayShow={100}
                delayHide={100}
                clickable
                render={({ content }) => {
                    const isLongContent = content && content?.length > 90;
                    return (
                        <div style={{ maxWidth: isLongContent ? '380px' : '220px' }}>
                            {content}
                        </div>
                    );
                }}
            />
        </div>
    )
}

export default BiometericDeviceConfiguration;